import {
	Vue, Component, Ref, toNative,
} from 'vue-facing-decorator';
import * as DB from 'interfaces/database';
import CountryFlagGlob from 'components/Tabulator/CountryFlag';
import mitt from 'mitt';
import { TabEvent } from 'interfaces/app';
import { CellComponentExtended, ColumnDefinitionExtended, TabulatorFull as Tabulator } from 'tabulator-tables';
import { createInstance } from 'utils/vue';
import TabulatorBtn from 'components/Tabulator/TabulatorBtn';
import CountryService from 'services/CountryService';
import Template from './template.vue';

const eventBus = mitt<TabEvent<DB.CountryModel>>();
@Component({
	components: {
		TabulatorBtn,
		CountryFlagGlob,
	},
	emits: ['routeCountry'],
	mixins: [Template],
})
class CountryTable extends Vue {
	@Ref('countryTable')
	private readonly tableReference!: HTMLDivElement;

	private isLoaded = false;

	private table?: Tabulator;

	private columnDefs: ColumnDefinitionExtended[] = [];

	private get loggedIn(): boolean {
		return this.$auth0.isAuthenticated.value;
	}

	protected beforeMount(): void {
		this.columnDefs = [
			{
				field: 'iso',
				title: 'ISO',
			},
			{
				title: 'Name',
				field: 'name',
				formatter: (cell: CellComponentExtended<DB.CountryModel>) => {
					const data = cell.getData();
					const instance = createInstance({
						component: CountryFlagGlob,
						props: {
							data,
						},
					});
					return instance;
				},
				headerFilter: true,
			},
			{
				title: 'Region',
				field: 'regionid',
			},
			{
				title: 'Default Currency',
				field: 'currency',
			},
			{
				title: 'Actions',
				formatter: (cell: CellComponentExtended<DB.BadgeModel>) => {
					const instance = createInstance({
						component: TabulatorBtn,
						props: {
							data: cell.getData(),
							buttons: [
								{
									id: 'edit',
									eventName: 'editItem',
									className: 'fa-edit',
								},
							],
							eventBus,
						},
					});
					return instance;
				},
			},
		] as ColumnDefinitionExtended[];
	}

	protected mounted(): void {
		eventBus.on(
			'editItem',
			this.editCountry,
		);
		this.tableInitialization();
		this.table?.on(
			'tableBuilt',
			this.onTableBuilt,
		);
	}

	protected beforeUnmount(): void {
		eventBus.off(
			'editItem',
			this.editCountry,
		);
		this.table?.off(
			'tableBuilt',
			this.onTableBuilt,
		);
		this.table?.destroy();
	}

	private onTableBuilt(): void {
		this.getData();
	}

	private tableInitialization(): void {
		this.table = new Tabulator(
			this.tableReference,
			{
				height: '60vh',
				layout: 'fitColumns',
				columns: this.columnDefs,
			},
		);
	}

	private async getData(): Promise<void> {
		const parameter = new URLSearchParams({
			limit: '0',
		});
		this.table?.alert('Loading');
		try {
			const data = await CountryService.getAll(parameter);
			this.table?.setData(data);
		} catch (err: any) {
			this.$toastError(err.message);
		} finally {
			this.table?.clearAlert();
		}
	}

	private editCountry(data: TabEvent<DB.CountryModel>['editItem']): void {
		this.$emit(
			'routeCountry',
			data.id,
		);
	}
}

export default toNative(CountryTable);
